import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Dialog from '@material-ui/core/Dialog';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import TextField from '@components/TextFieldCaseSetting';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import { DropdownInput } from '@trueprodigy/components';
import { userSelector } from '@modules/bi/selectors/search';
import { showError } from '@actions/global';

import { usePropertyActions } from '@modules/appraisal/hooks/usePropertyActions';

const CreateWorkItem = ({
  open,
  handleClose,
  generateIndividual,
}) => {
  const dispatch = useDispatch();
  const [note, setNote] = useState('');
  const [itemType, setItemType] = useState(null);
  const [itemSubtype, setItemSubtype] = useState(null);
  const [typeOptionData, setTypeOptionData] = useState([]);
  const [workItemData, setWorkItemData] = useState(null);
  const [workItemDataLoading, setWorkItemDataLoading] = useState(false);

  const {
    fetchWorkItemTypes,
    insertWorkItem,
  } = usePropertyActions([]);

  const userData = useSelector(userSelector);

  useEffect(() => {
    if (open) {
      initializeData();
    } else {
      setTypeOptionData([]);
      setItemType(null);
      setItemSubtype(null);
      setWorkItemData(null);
      setNote('');
    }
  }, [open]);

  const initializeData = async () => {
    let data = await fetchWorkItemTypes();
    if (data && data.results && data.results.length > 0) {
      setTypeOptionData(data.results);
    }
  };

  const handleCreate = async () => {
    let payload = {
      workItemDefinitionID: itemType,
      workItemStatusID: typeOptionData.filter(
        r => r.definitionID === itemType,
      )[0].initialStatusID,
      workItemSubTypeID: itemSubtype,
      contactName: userData.name,
      contactPhone: '',
      contactEmail: userData.email,
      contactNotes: note,
      numRealProperty: 0,
      numPersonalProperty: 0,
      numMobileHomeProperty: 0,
      numMineralProperty: 0,
    };
    setWorkItemDataLoading(true);
    try {
      await insertWorkItem({ payload });
      setWorkItemDataLoading(false);
      handleClose();
    } catch (error) {
      dispatch(showError(error));
      setWorkItemDataLoading(false);
    }
  };

  const subtypeOptions = itemType
    ? typeOptionData.filter(r => r.definitionID === itemType)[0].subTypes
    : [];

  return (
    <Dialog
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={open}
      maxWidth={false}
    >
      <TitleWrapper>
        <span style={{ paddingLeft: '10px' }}>Create Work Item</span>
        <IconButton onClick={handleClose} style={{ padding: 7 }}>
          <CloseIcon />
        </IconButton>
      </TitleWrapper>
      <ContainerWrapper>
        <div>
          <div style={{ display: 'flex' }}>
            <span
              style={{
                width: 150,
                display: 'inline-block',
                alignSelf: 'flex-end',
              }}
            >
              Work Item Type:{' '}
            </span>
            <div style={{ flexGrow: 1 }}>
              <DropdownWrapper>
                <DropdownInput
                  options={typeOptionData.map(r => {
                    return {
                      label: r.definitionType,
                      value: r.definitionID,
                    };
                  })}
                  disabled={workItemData}
                  addEmptyOption={false}
                  placeholder={'Enter Item Type...'}
                  input={{
                    onChange: selection => {
                      setItemType(selection.value);
                      setItemSubtype(null);
                    },
                    value: itemType,
                  }}
                  meta={{
                    touched: () => {},
                  }}
                />
              </DropdownWrapper>
            </div>
          </div>
          <div style={{ display: 'flex' }}>
            <span
              style={{
                width: 150,
                display: 'inline-block',
                alignSelf: 'flex-end',
              }}
            >
              Work Item Subtype:{' '}
            </span>
            <div style={{ flexGrow: 1 }}>
              <DropdownWrapper>
                <DropdownInput
                  disabled={!itemType || workItemData}
                  options={subtypeOptions.map(r => {
                    return {
                      label: r.workItemSubType,
                      value: r.subTypeID,
                    };
                  })}
                  addEmptyOption={false}
                  placeholder={'Enter Item Subtype...'}
                  input={{
                    onChange: selection => setItemSubtype(selection.value),
                    value: itemSubtype,
                  }}
                  meta={{
                    touched: () => {},
                  }}
                />
              </DropdownWrapper>
            </div>
          </div>
          <div>
            <TextField
              style={{
                width: '100%',
                marginTop: 20,
                minHeight: 100,
              }}
              disabled={workItemData}
              multiline
              rows="10"
              name="note"
              label="Note"
              value={note}
              onChange={({ target: { value } }) => setNote(value)}
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </div>
        </div>
      </ContainerWrapper>
      <ButtonsWrapper
        style={{
          justifyContent: generateIndividual ? 'space-between' : 'flex-end',
        }}
      >
        <Button
          color="primary"
          disabled={!itemType || !itemSubtype || workItemData}
          onClick={handleCreate}
        >
          {'CREATE'}
        </Button>
      </ButtonsWrapper>
      {!workItemData && workItemDataLoading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '20px 0 40px 0',
          }}
        >
          <CircularProgress />
        </div>
      ) : null}
    </Dialog>
  );
};

CreateWorkItem.propTypes = {
  propertyData: PropTypes.any,
  history: PropTypes.any,
  open: PropTypes.bool,
  generateIndividual: PropTypes.bool,
  handleClose: PropTypes.func.isRequired,
};

const ContainerWrapper = styled.div`
  width: 800px;
  padding: 0 20px;
`;

const TitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px 10px 0 10px;

  > span {
    font-size: 20px;
    font-weight: bold;
  }
`;

const ButtonsWrapper = styled.div`
  padding: 0 20px 10px 20px;
  display: flex;
  justify-content: space-between;
`;

const DropdownWrapper = styled.div`
  > div {
    width: 100%;
  }

  > div > div > div:first-child {
    width: 100%;
  }
`;

export default withRouter(CreateWorkItem);
